import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "../../../components/DashboardLayout";
import {
  getStudentsAPI,
  giveFreeAccessAPI,
  getStudentByNumberAPI,
  getStudentsByClassStatusAndDateAPI,
  unassignAccessAPI,
} from "../../../services/portal/admin/studentService";
import { getClassesAPI } from "../../../services/portal/admin/classService";
import {
  showErrorToastMsg,
  showSuccessToastMsg,
} from "../../../utility/ToastMessage";
import Loader from "../../../components/Loader";
import {
  formatDate,
  getMonthNameFromDate,
  tableHeaderColor,
} from "../../../utility/Common";
import ClassDetailsBox from "../../../components/portal/ClassDetailsBox";
import { RoutePaths } from "../../../utility/RoutePaths";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { getPayedInformationAPI } from "../../../services/portal/student/studentService";
import RemoveConfirmationDialog from "../../../components/portal/RemoveConfirmationDialog";
import MessageModal from "../../../components/portal/MessageModal";

const ITEMS_PER_PAGE = 10;

const StudentListPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const classId = searchParams.get("classId");
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);

  const [loadingAllStudents, setLoadingAllStudents] = useState(false);
  const [isShowAllStudents, setShowAllStudents] = useState(false);
  const [allStudents, setAllStudents] = useState([]);
  const [filteredAllStudents, setFilteredAllStudents] = useState([]);

  const [classes, setClasses] = useState([]);
  const [classDetails, setClassDetails] = useState(null);
  const [selectedClassId, setSelectedClassId] = useState(
    classId ? classId : -1,
  );
  const [selectedClassName, setSelectedClassName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchQueryAllStudents, setSearchQueryAllStudents] = useState("");
  const [searchByMobileNumber, setSearchByMobileNumber] = useState("");
  const [searchByMobileLoading, setSearchByMobileLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageAllStudents, setCurrentPageAllStudents] = useState(1);
  const [showGiveAccessDialog, setShowGiveAccessDialog] = useState(false);
  const [giveAccessStudent, setGiveAccessStudent] = useState(null);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedAvailableMonth, setSelectedAvailableMonth] = useState("");
  const [giveAccessLoading, setGiveAccessLoading] = useState(false);
  const [showOnlyNotAssignedStudents, setShowOnlyNotAssignedStudents] =
    useState(false);

  const [showRemoveAccessConfirmDialog, setShowRemoveAccessConfirmDialog] =
    useState(false);
  const [selectedRemoveAccessStudent, setSelectedRemoveAccessStudent] =
    useState(null);

  const [showSmsModal, setShowSmsModal] = useState(false);
  const [smsStudents, setSmsStudents] = useState([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [selectedAllStudentIds, setSelectedAllStudentIds] = useState([]);

  const [filterBatch, setFilterBatch] = useState("");
  const [filterInstitute, setFilterInstitute] = useState("");
  const [filterBatchAllStudents, setFilterBatchAllStudents] = useState("");
  const [filterInstituteAllStudents, setFilterInstituteAllStudents] = useState("");

  const BATCHES = ["Batch 2026", "Batch 2027", "Batch 2028"];
  const INSTITUTES = [
    "Science Center - Kegalle",
    "Sesip - Rambukkana",
    "Unique - Matale",
  ];

  useEffect(() => {
    loadClasses();
  }, []);

  useEffect(() => {
    const query = searchQuery.trim().toLowerCase();
    const filtered = students.filter((student) => {
      if (filterBatch && student.Batch !== filterBatch) return false;
      if (filterInstitute && student.Institute !== filterInstitute) return false;
      if (!query) return true;
      const paymentType = (student.PaymentType === "Free Card By Admin" ? "Added By Admin" : student.PaymentType).toLowerCase();
      const name =
        `${student.FirstName || ""} ${student.LastName || ""}`.toLowerCase();
      const email = (student.Email || "").toLowerCase();
      const mobile = (student.MobileNo || "").toLowerCase();
      const studentId = (student.StudentId || "").toLowerCase();
      const city = (student.City || "").toLowerCase();
      return (
        name.includes(query) ||
        email.includes(query) ||
        mobile.includes(query) ||
        studentId.includes(query) ||
        paymentType.includes(query) ||
        city.includes(query)
      );
    });
    setFilteredStudents(filtered);
    setCurrentPage(1);
  }, [searchQuery, students, filterBatch, filterInstitute]);

  useEffect(() => {
    const query = searchQueryAllStudents.trim().toLowerCase();
    let filtered = allStudents;
    if (showOnlyNotAssignedStudents) {
      filtered = allStudents.filter(
        (itemA) => !students.some((itemB) => itemB.UserId === itemA.UserId),
      );
    }
    filtered = filtered.filter((student) => {
      if (filterBatchAllStudents && student.Batch !== filterBatchAllStudents) return false;
      if (filterInstituteAllStudents && student.Institute !== filterInstituteAllStudents) return false;
      if (!query) return true;
      const paymentType = (student.PaymentType || "").toLowerCase();
      const name =
        `${student.FirstName || ""} ${student.LastName || ""}`.toLowerCase();
      const email = (student.Email || "").toLowerCase();
      const mobile = (student.MobileNo || "").toLowerCase();
      const studentId = (student.StudentId || "").toLowerCase();
      const city = (student.City || "").toLowerCase();
      return (
        name.includes(query) ||
        email.includes(query) ||
        mobile.includes(query) ||
        studentId.includes(query) ||
        city.includes(query)
      );
    });
    setFilteredAllStudents(filtered);
    setCurrentPageAllStudents(1);
  }, [searchQueryAllStudents, allStudents, showOnlyNotAssignedStudents, filterBatchAllStudents, filterInstituteAllStudents]);

  useEffect(() => {
    if (classes.length > 0) {
      const classItem = classes.find(
        (c) => c.ClassId === Number(selectedClassId),
      );
      if (classItem) {
        setClassDetails(classItem);
      } else {
        setClassDetails(null);
      }

      setAvailableMonths([]);
      setSelectedAvailableMonth("");
      loadClassSchedules();
    }
  }, [selectedClassId, classes]);

  useEffect(() => {
    if (availableMonths && availableMonths.length > 0) {
      const date = formatDate(availableMonths[0].FirstDayOfMonth);
      setSelectedAvailableMonth(date);
    }
  }, [availableMonths]);

  useEffect(() => {
    if (selectedAvailableMonth.length > 0) {
      loadStudents();
    }
  }, [selectedAvailableMonth]);

  const loadClasses = async () => {
    try {
      const res = await getClassesAPI();
      if (res && res.IsSuccessful && res.Data) {
        setClasses(res.Data);

        if (res.Data.length > 0) {
          setSelectedClassId(res.Data[0].ClassId);
          setSelectedClassName(res.Data[0].Name);
        }
      }
    } catch (error) {
      console.error("Error loading classes:", error);
    }
  };

  const loadClassSchedules = async () => {
    try {
      const res = await getPayedInformationAPI(selectedClassId);
      if (res && res.IsSuccessful && res.Data) {
        setAvailableMonths(res.Data);
      }
    } catch (error) {
      console.error("Error loading classes:", error);
    }
  };

  const loadStudents = async () => {
    if (selectedClassId > 0 && selectedAvailableMonth !== "") {
      try {
        setLoading(true);
        const res = await getStudentsByClassStatusAndDateAPI(
          selectedClassId,
          2,
          selectedAvailableMonth,
        );
        if (res && res.IsSuccessful && res.Data) {
          setStudents(res.Data || []);
        } else {
          setStudents([]);
        }
      } catch (error) {
        console.error("Error loading students:", error);
        showErrorToastMsg("Failed to load students");
        setStudents([]);
      } finally {
        setLoading(false);
      }
    }
  };

  const showAllStudents = async () => {
    setLoadingAllStudents(true);
    setShowAllStudents(true);
    const res = await getStudentsAPI(0);
    if (res && res.IsSuccessful && res.Data) {
      setAllStudents(res.Data || []);
    } else {
      setAllStudents([]);
    }
    setLoadingAllStudents(false);
  };

  const exportToExcel = () => {
    var list = [];
    students.map((item) => {
      list.push({
        StudentId: item.StudentId,
        FirstName: item.FirstName,
        LastName: item.LastName,
        Email: item.Email,
        NIC: item.NIC,
        AddressLine1: item.AddressLine1,
        Street: item.Street,
        City: item.City,
        MobileNo: item.MobileNo,
        GuardianContactNo: item.GuardianContactNo,
      });
    });

    // Convert JSON to worksheet
    const worksheet = XLSX.utils.json_to_sheet(list);

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      classDetails?.name + "-students-details" ?? "Sheet1",
    );

    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    saveAs(
      blob,
      classDetails?.Name + "-students-details.xlsx" ?? "report.xlsx",
    );
  };

  const handleClassFilterChange = (e) => {
    setSelectedClassId(Number(e.target.value));
    setSelectedClassName(e.target.options[e.target.selectedIndex].text);
  };

  const handleViewStudent = (student) => {
    const studentId =
      student.Id || student.id || student.StudentId || student.studentId;
    navigate(`${RoutePaths.ADMIN_STUDENT_DETAILS_PAGE}?studentId=${studentId}`);
  };

  const handleSearchByMobile = async () => {
    const number = searchByMobileNumber.trim();
    if (!number) {
      showErrorToastMsg("Enter a mobile number");
      return;
    }
    setSearchByMobileLoading(true);
    try {
      const res = await getStudentByNumberAPI(number);
      if (res && res.IsSuccessful && res.Data) {
        openGiveAccessDialog(res.Data);
      } else {
        showErrorToastMsg(res?.Message || "Student not found");
      }
    } catch (error) {
      console.error("Error searching by mobile:", error);
      showErrorToastMsg("Failed to search student");
    } finally {
      setSearchByMobileLoading(false);
    }
  };

  const openGiveAccessDialog = async (student) => {
    setGiveAccessStudent(student);
    setShowGiveAccessDialog(true);
  };

  const closeGiveAccessDialog = () => {
    setShowGiveAccessDialog(false);
    setGiveAccessStudent(null);
  };

  const confirmGiveAccess = async () => {
    if (!giveAccessStudent || !selectedAvailableMonth || !selectedClassId)
      return;
    setGiveAccessLoading(true);
    try {
      var params = {
        date: selectedAvailableMonth,
        classId: Number(selectedClassId),
        userId: Number(giveAccessStudent.UserId),
        isPayed: true,
        isFreePass: true,
      };
      console.log(params);
      const res = await giveFreeAccessAPI(params);
      if (res && res.IsSuccessful) {
        showSuccessToastMsg("Access granted successfully");
        closeGiveAccessDialog();
        loadStudents();
      } else {
        showErrorToastMsg(res.Message || "Failed to give access");
      }
    } catch (error) {
      console.error("Error giving access:", error);
      showErrorToastMsg("Failed to give access");
    } finally {
      setGiveAccessLoading(false);
    }
  };

  const confirmRemoveAccess = async () => {
    var params = {
      date: selectedAvailableMonth,
      classId: Number(selectedClassId),
      userId: Number(selectedRemoveAccessStudent.UserId),
    };
    const res = await unassignAccessAPI(params);
    if (res && res.IsSuccessful) {
      showSuccessToastMsg("Access removed successfully");
      cancelRemoveAccess();
      loadStudents();
    } else {
      showErrorToastMsg(res.Message || "Failed to give access");
    }
  };

  const showRemoveAccessDialog = async (student) => {
    setSelectedRemoveAccessStudent(student);
    setShowRemoveAccessConfirmDialog(true);
  };

  const cancelRemoveAccess = () => {
    setSelectedRemoveAccessStudent(null);
    setShowRemoveAccessConfirmDialog(false);
  };

  const toggleStudentSelection = (userId) => {
    setSelectedStudentIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const isAllFilteredSelected =
    filteredStudents.length > 0 &&
    filteredStudents.every((s) => selectedStudentIds.includes(s.UserId));

  const toggleSelectAll = () => {
    if (isAllFilteredSelected) {
      setSelectedStudentIds([]);
    } else {
      setSelectedStudentIds(filteredStudents.map((s) => s.UserId));
    }
  };

  const openSelectedSmsModal = () => {
    const selected = students.filter((s) =>
      selectedStudentIds.includes(s.UserId)
    );
    setSmsStudents(selected);
    setShowSmsModal(true);
  };

  const toggleAllStudentSelection = (userId) => {
    setSelectedAllStudentIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const isAllFilteredAllStudentsSelected =
    filteredAllStudents.length > 0 &&
    filteredAllStudents.every((s) => selectedAllStudentIds.includes(s.UserId));

  const toggleSelectAllStudents = () => {
    if (isAllFilteredAllStudentsSelected) {
      setSelectedAllStudentIds([]);
    } else {
      setSelectedAllStudentIds(filteredAllStudents.map((s) => s.UserId));
    }
  };

  const openSelectedAllStudentsSmsModal = () => {
    const selected = allStudents.filter((s) =>
      selectedAllStudentIds.includes(s.UserId)
    );
    setSmsStudents(selected);
    setShowSmsModal(true);
  };

  const totalPages = Math.ceil(filteredStudents.length / ITEMS_PER_PAGE) || 1;
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedStudents = filteredStudents.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE,
  );

  const totalPagesAllStudents =
    Math.ceil(filteredAllStudents.length / ITEMS_PER_PAGE) || 1;
  const startIndexAllStudents = (currentPageAllStudents - 1) * ITEMS_PER_PAGE;
  const paginatedAllStudents = filteredAllStudents.slice(
    startIndexAllStudents,
    startIndexAllStudents + ITEMS_PER_PAGE,
  );

  return (
    <DashboardLayout
      title="Student List"
      subtitle="View and manage all students"
    >
      <div>
        {/* Filter Section */}
        <div className="bg-white border border-gray-200 rounded-lg mb-6 p-4">
          <div className="flex flex-wrap items-center gap-4">
            <label className="block text-sm font-medium text-gray-700">
              Filter by Class and Month:
            </label>
            <select
              value={selectedClassId}
              onChange={handleClassFilterChange}
              className="text-sm font-medium px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[250px]"
            >
              <option value="-1">Select Class</option>
              {classes.map((classItem) => (
                <option key={classItem.ClassId} value={classItem.ClassId}>
                  {classItem.Name}
                </option>
              ))}
            </select>

            <select
              value={selectedAvailableMonth}
              onChange={(e) => setSelectedAvailableMonth(e.target.value)}
              className="text-sm font-medium px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[250px]"
            >
              {availableMonths &&
                availableMonths.map((m) => {
                  const date = formatDate(m.FirstDayOfMonth);
                  return (
                    <option key={date} value={date}>
                      {m.MonthYear}
                    </option>
                  );
                })}
            </select>
          </div>
        </div>

        {classDetails && <ClassDetailsBox classDetails={classDetails} />}

        {/* Search by mobile number */}
        <div className="bg-white border border-gray-200 rounded-lg mb-4 p-4 flex justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <h4 className="text-sm font-medium text-gray-700 mb-3">
              Search student by mobile number to Give Access
            </h4>
            <input
              type="text"
              placeholder="Enter mobile number"
              value={searchByMobileNumber}
              onChange={(e) => setSearchByMobileNumber(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearchByMobile()}
              className="text-sm px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
            />
            <button
              type="button"
              onClick={handleSearchByMobile}
              disabled={searchByMobileLoading}
              className="text-sm font-medium px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {searchByMobileLoading ? "Searching..." : "Search"}
            </button>
          </div>

          <button
            type="button"
            onClick={() => showAllStudents()}
            disabled={searchByMobileLoading}
            className="text-sm font-medium px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
          >
            Show All Students
          </button>
        </div>

        {/* Students Table */}
        <div className="bg-white border border-gray-200 rounded-lg mb-6 overflow-hidden">
          <div
            className={`${tableHeaderColor} px-6 py-4 border-b border-gray-200`}
          >
            <h4 className="text-md font-medium text-white">
              {selectedClassName} Students List ({filteredStudents.length})
            </h4>
          </div>
          <div className="overflow-x-auto">
            <div className="flex justify-between gap-4 p-5">
              <button
                onClick={openSelectedSmsModal}
                type="button"
                disabled={selectedStudentIds.length === 0}
                className="text-sm font-medium px-4 py-2 rounded-md transition-colors bg-green-600 text-white hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Send SMS{selectedStudentIds.length > 0 ? ` (${selectedStudentIds.length})` : ""}
              </button>
              <div className="flex flex-wrap gap-3">
                <select
                  value={filterBatch}
                  onChange={(e) => setFilterBatch(e.target.value)}
                  className="text-sm px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Batches</option>
                  {BATCHES.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
                <select
                  value={filterInstitute}
                  onChange={(e) => setFilterInstitute(e.target.value)}
                  className="text-sm px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Institutes</option>
                  {INSTITUTES.map((inst) => (
                    <option key={inst} value={inst}>{inst}</option>
                  ))}
                </select>
                <div className="flex-1 min-w-[300px] max-w-md">
                  <input
                    type="text"
                    placeholder="Search by name, email, mobile, student ID, city..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <button
                    onClick={() => exportToExcel()}
                    type="button"
                    className={`text-sm font-medium px-4 py-2 rounded-md transition-colors bg-primary-600 text-white hover:bg-primary-700"
                `}
                  >
                    Export
                  </button>
                </div>
              </div>
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={isAllFilteredSelected}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mobile Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Batch
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Institute
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Registered Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="text-sm font-medium px-6 py-4 text-center text-gray-500"
                    >
                      <Loader />
                    </td>
                  </tr>
                ) : filteredStudents.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="text-sm font-medium px-6 py-4 text-center text-gray-500"
                    >
                      No students found.
                    </td>
                  </tr>
                ) : (
                  paginatedStudents.map((student) => {
                    return (
                      <tr key={student.UserId} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <input
                            type="checkbox"
                            checked={selectedStudentIds.includes(student.UserId)}
                            onChange={() => toggleStudentSelection(student.UserId)}
                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {student.FirstName + " " + student.LastName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {student.PaymentType === "Free Card By Admin" ? "Added By Admin" : student.PaymentType}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {student.MobileNo}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {student.Email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {student.StudentId}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {student.Batch}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {student.Institute}
                        </td>
                        <td className="px-6 py-4 whitespace-wrap text-sm text-gray-900">
                          {student.AddressLine1 + ", " + student.Street + ", " + student.City}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {student.CreatedAt
                            ? formatDate(student.CreatedAt)
                            : ""}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex gap-4">
                          <button
                            onClick={() => showRemoveAccessDialog(student)}
                            className="text-red-600 hover:text-red-900 font-medium"
                          >
                            Remove Access
                          </button>

                          <button
                            onClick={() => handleViewStudent(student)}
                            className="text-primary-600 hover:text-primary-900 font-medium"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {filteredStudents.length > 0 && totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm text-gray-600">
                Showing {startIndex + 1} to{" "}
                {Math.min(startIndex + ITEMS_PER_PAGE, filteredStudents.length)}{" "}
                of {filteredStudents.length} results
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => {
                    if (totalPages <= 7) return true;
                    if (p === 1 || p === totalPages) return true;
                    if (Math.abs(p - currentPage) <= 1) return true;
                    return false;
                  })
                  .map((p, idx, arr) => (
                    <React.Fragment key={p}>
                      {idx > 0 && arr[idx - 1] !== p - 1 && (
                        <span className="px-2 text-gray-400">…</span>
                      )}
                      <button
                        onClick={() => setCurrentPage(p)}
                        className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                          currentPage === p
                            ? "bg-primary-600 text-white"
                            : "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  ))}
                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {isShowAllStudents && (
          <div className="bg-white border border-gray-200 rounded-lg mb-6 overflow-hidden">
            <div
              className={`${tableHeaderColor} px-6 py-4 border-b border-gray-200`}
            >
              <h4 className="text-md font-medium text-white">
                All Students List ({filteredAllStudents.length})
              </h4>
            </div>
            <div className="overflow-x-auto">
              <div className="flex justify-between gap-4 p-5">
                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      onChange={(e) =>
                        setShowOnlyNotAssignedStudents(e.target.checked)
                      }
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-gray-700">
                      Show only not assigned students
                    </span>
                  </label>
                  <button
                    onClick={openSelectedAllStudentsSmsModal}
                    type="button"
                    disabled={selectedAllStudentIds.length === 0}
                    className="text-sm font-medium px-4 py-2 rounded-md transition-colors bg-green-600 text-white hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Send SMS{selectedAllStudentIds.length > 0 ? ` (${selectedAllStudentIds.length})` : ""}
                  </button>
                </div>

              <div className="flex flex-wrap gap-3">
                <select
                  value={filterBatchAllStudents}
                  onChange={(e) => setFilterBatchAllStudents(e.target.value)}
                  className="text-sm px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Batches</option>
                  {BATCHES.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
                <select
                  value={filterInstituteAllStudents}
                  onChange={(e) => setFilterInstituteAllStudents(e.target.value)}
                  className="text-sm px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Institutes</option>
                  {INSTITUTES.map((inst) => (
                    <option key={inst} value={inst}>{inst}</option>
                  ))}
                </select>
                <div className="flex-1 min-w-[200px] max-w-md">
                  <input
                    type="text"
                    placeholder="Search by name, email, mobile, student ID, city..."
                    value={searchQueryAllStudents}
                    onChange={(e) => setSearchQueryAllStudents(e.target.value)}
                    className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                </div>
              </div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={isAllFilteredAllStudentsSelected}
                        onChange={toggleSelectAllStudents}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mobile Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Student ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Batch
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Institute
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      City
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Registered Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loadingAllStudents ? (
                    <tr>
                      <td
                        colSpan="7"
                        className="text-sm font-medium px-6 py-4 text-center text-gray-500"
                      >
                        <Loader />
                      </td>
                    </tr>
                  ) : filteredAllStudents.length === 0 ? (
                    <tr>
                      <td
                        colSpan="7"
                        className="text-sm font-medium px-6 py-4 text-center text-gray-500"
                      >
                        No students found.
                      </td>
                    </tr>
                  ) : (
                    paginatedAllStudents.map((student) => {
                      return (
                        <tr key={student.UserId} className="hover:bg-gray-50">
                          <td className="px-4 py-4">
                            <input
                              type="checkbox"
                              checked={selectedAllStudentIds.includes(student.UserId)}
                              onChange={() => toggleAllStudentSelection(student.UserId)}
                              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {student.FirstName + " " + student.LastName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {student.MobileNo}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {student.Email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {student.StudentId}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {student.Batch}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {student.Institute}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {student.City}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {student.CreatedAt
                              ? formatDate(student.CreatedAt)
                              : ""}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex gap-4">
                            <button
                              onClick={() => openGiveAccessDialog(student)}
                              className="text-green-600 hover:text-green-900 font-medium"
                            >
                              Give Access
                            </button>
                            <button
                              onClick={() => handleViewStudent(student)}
                              className="text-primary-600 hover:text-primary-900 font-medium"
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            {filteredAllStudents.length > 0 && totalPagesAllStudents > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm text-gray-600">
                  Showing {startIndexAllStudents + 1} to{" "}
                  {Math.min(
                    startIndexAllStudents + ITEMS_PER_PAGE,
                    filteredAllStudents.length,
                  )}{" "}
                  of {filteredAllStudents.length} results
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() =>
                      setCurrentPageAllStudents((p) => Math.max(1, p - 1))
                    }
                    disabled={currentPageAllStudents === 1}
                    className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {Array.from(
                    { length: totalPagesAllStudents },
                    (_, i) => i + 1,
                  )
                    .filter((p) => {
                      if (totalPagesAllStudents <= 7) return true;
                      if (p === 1 || p === totalPagesAllStudents) return true;
                      if (Math.abs(p - currentPageAllStudents) <= 1)
                        return true;
                      return false;
                    })
                    .map((p, idx, arr) => (
                      <React.Fragment key={p}>
                        {idx > 0 && arr[idx - 1] !== p - 1 && (
                          <span className="px-2 text-gray-400">…</span>
                        )}
                        <button
                          onClick={() => setCurrentPageAllStudents(p)}
                          className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                            currentPageAllStudents === p
                              ? "bg-primary-600 text-white"
                              : "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {p}
                        </button>
                      </React.Fragment>
                    ))}
                  <button
                    onClick={() =>
                      setCurrentPageAllStudents((p) =>
                        Math.min(totalPagesAllStudents, p + 1),
                      )
                    }
                    disabled={currentPageAllStudents === totalPagesAllStudents}
                    className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Give Access Dialog */}
      {showGiveAccessDialog && giveAccessStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Give Access for {selectedClassName} -{" "}
              {getMonthNameFromDate(selectedAvailableMonth)}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {giveAccessStudent.FirstName} {giveAccessStudent.LastName} (
              {giveAccessStudent.StudentId})
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={closeGiveAccessDialog}
                disabled={giveAccessLoading}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmGiveAccess}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 font-medium"
              >
                {giveAccessLoading ? "Processing..." : "Give Access"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSmsModal && (
        <MessageModal
          students={smsStudents}
          className={selectedClassName}
          month={selectedAvailableMonth}
          onClose={() => setShowSmsModal(false)}
        />
      )}

      {showRemoveAccessConfirmDialog && (
        <RemoveConfirmationDialog
          loading={loading}
          description={
            selectedRemoveAccessStudent.FirstName +
            " " +
            selectedRemoveAccessStudent.LastName
          }
          title="Confirm"
          mainDescription="Are you sure, you want to remove access this student?"
          cancelDelete={cancelRemoveAccess}
          confirmDelete={confirmRemoveAccess}
        />
      )}
    </DashboardLayout>
  );
};

export default StudentListPage;
